import { GetMerchantCustomizationsResponse } from 'containers/settings/settings.model';
import { InvoicePayloadV2 } from '../invoices.model';
import { InvoicesEditState } from './invoicesEditSlice';
import { InvoiceSourceEnum, InvoiceVersion } from '../constants';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import { constructArrayObjectAsString } from 'utils/commonUtils';
import {
  getPaymentModes,
  prepareLineItemListV2,
  prepareServiceChargeListRequestPayloadV2,
  prepareSummatedUniqueTaxListV2,
  prepareV2DiscountPayload,
  prepareV2ServiceFeePayload,
  prepareV2TipPayload,
  transformInvoiceRemindersToApiFormat
} from '../create-invoice/util';
import {
  getAttachmentArray,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  prepareDiscountRelatedPayload
} from '../utils';

// Main V2 Edit Invoice Payload Function
export const getEditInvoiceV2Payload = (
  selectedInvoice: InvoicesEditState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  sendEmailToCustomer?: boolean
): InvoicePayloadV2 => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  const invoicePayload: InvoicePayloadV2 = {
    customerId: selectedInvoice?.invoiceCustomerEditPage?.ID,
    customerServiceAddressId: selectedInvoice.invoiceCustomerEditPage?.serviceAddress?.[0]?.id ?? undefined,
    invoiceDate: selectedInvoice.invoiceOptionsEditPage?.invoiceDate,
    invoiceDueDate: selectedInvoice.invoiceOptionsEditPage?.dueDate,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItemsEditPage),
    lineItemAmount: selectedInvoice.lineItemsTotalEditPage,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormDataEditPage),
    subTotalAmount: selectedInvoice.invoiceSubTotalEditPage,
    serviceFee: prepareV2ServiceFeePayload({
      isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabledEditPage,
      currentSurcharge: selectedInvoice.currentSurchargeEditPage
    } as any),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItemsEditPage,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmountEditPage,
    note: selectedInvoice.commentEditPage || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachmentsEditPage),
    paymentMethodList: getPaymentModes(selectedInvoice.invoicePaymentModesEdit, merchantCustomisations.paymentModes),
    tip: prepareV2TipPayload({
      tipEnabled: selectedInvoice.tipEnabledEditPage,
      tipTiming: selectedInvoice.tipTimingEditPage
    } as any),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabledEditPage,
    reminderEnabled: selectedInvoice.paymentRemindersEnabledEditPage,
    reminders: transformInvoiceRemindersToApiFormat(selectedInvoice.paymentRemindersEditPage) ?? [],
    signatureEnabled: selectedInvoice.isSignatureEnabledEditPage,
    tncId: tncData.tncId,
    referenceId: selectedInvoice.invoiceReferenceNumberEditPage,
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB,
    sendEmailToCustomer: sendEmailToCustomer
  };

  // Analytics logging
  const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
  logAnalyticEvent(CleverTapEventsInvoices.webInvoicesEditInvoiceSave, {
    customerName: selectedInvoice?.invoiceCustomerEditPage?.name,
    customerId: selectedInvoice?.invoiceCustomerEditPage?.ID,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    amount: selectedInvoice.invoiceSubTotalEditPage.toString(),
    'Line Items': constructArrayObjectAsString(
      getLineItemsArrayForEvent(selectedInvoice.invoiceLineItemsEditPage),
      'Line item'
    ),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormDataEditPage),
    surchargePercentage: selectedInvoice.isServiceFeeEnabledEditPage
      ? selectedInvoice.currentSurchargeEditPage.percentageWithBps ?? '0'
      : '0',
    ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
    serviceAddressId: selectedInvoice.invoiceCustomerEditPage?.serviceAddress?.[0]?.id ?? ''
  });

  return invoicePayload;
};

// PDF Preview Payload for Edit Invoice
export const getPDFPreviewPayloadEdit = (
  selectedInvoice: InvoicesEditState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  invoiceNumber: string
) => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  return {
    invoiceNumber,
    customerId: selectedInvoice?.invoiceCustomerEditPage?.ID,
    customerServiceAddressId: selectedInvoice.invoiceCustomerEditPage?.serviceAddress?.[0]?.id ?? undefined,
    invoiceDate: selectedInvoice.invoiceOptionsEditPage?.invoiceDate,
    invoiceDueDate: selectedInvoice.invoiceOptionsEditPage?.dueDate,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItemsEditPage),
    lineItemAmount: selectedInvoice.lineItemsTotalEditPage,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormDataEditPage),
    subTotalAmount: selectedInvoice.invoiceSubTotalEditPage,
    serviceFee: prepareV2ServiceFeePayload({
      isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabledEditPage,
      currentSurcharge: selectedInvoice.currentSurchargeEditPage
    } as any),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItemsEditPage,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmountEditPage,
    note: selectedInvoice.commentEditPage || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachmentsEditPage),
    tip: prepareV2TipPayload({
      tipEnabled: selectedInvoice.tipEnabledEditPage,
      tipTiming: selectedInvoice.tipTimingEditPage
    } as any),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabledEditPage,
    reminderEnabled: selectedInvoice.paymentRemindersEnabledEditPage,
    signatureEnabled: selectedInvoice.isSignatureEnabledEditPage,
    tncId: tncData.tncId,
    referenceId: selectedInvoice.invoiceReferenceNumberEditPage || '',
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB
  };
};
